import React, {
	createContext,
	useContext,
	useState,
	useMemo,
	useCallback,
} from "react";

import NotificationContainer from "../NotificationContainer";

const NotificationContext = createContext<
	NotificationProvider.NotificationContextProps | undefined
>(undefined);

const useNotification = () => {
	const context = useContext(NotificationContext);
	if (!context) {
		throw new Error(
			"useNotification must be used within a NotificationProvider",
		);
	}
	return context;
};

const NotificationProvider: React.FC<React.PropsWithChildren> = ({
	children,
}) => {
	const [notifications, setNotifications] = useState<
		NotificationProvider.Notification[]
	>([]);
	const [exitingNotificationId, setExitingNotificationId] = useState<
		number | null
	>(null);

	const showNotification = useCallback(
		(
			type: NotificationProvider.Notification["type"],
			message: string,
			description: string,
			isClose?: boolean,
		) => {
			const id = Date.now();
			setNotifications((prev) => [
				...prev,
				{ id, type, message, description, isClose },
			]);

			if (isClose) {
				setExitingNotificationId(id);
			} else {
				setTimeout(() => {
					setExitingNotificationId(id);
					setTimeout(() => {
						setNotifications((prev) =>
							prev.filter(
								(notification) => notification.id !== id,
							),
						);
						setExitingNotificationId(null);
					}, 500);
				}, 5000);
			}
		},
		[],
	);

	const value = useMemo(
		() => ({
			success: (
				message: string,
				description: string,
				isClose?: boolean,
			) => showNotification("success", message, description, isClose),
			info: (message: string, description: string, isClose?: boolean) =>
				showNotification("info", message, description, isClose),
			warn: (message: string, description: string, isClose?: boolean) =>
				showNotification("warn", message, description, isClose),
			error: (message: string, description: string, isClose?: boolean) =>
				showNotification("error", message, description, isClose),
		}),
		[showNotification],
	);

	return (
		<NotificationContext.Provider value={value}>
			{children}
			{notifications.map((notification, index) => (
				<NotificationContainer
					key={notification.id}
					type={notification.type}
					message={notification.message}
					description={notification.description}
					style={{
						top: `${index * 70 + 20}px`,
						marginTop: `${index * 15}px`,
					}}
					isExiting={
						notification?.isClose
							? !notification.isClose
							: notification.id === exitingNotificationId
					}
					onClose={(): void => {
						setNotifications((prev) =>
							prev.filter(
								(item) => notification?.id !== item?.id,
							),
						);
						setExitingNotificationId(null);
					}}
				/>
			))}
		</NotificationContext.Provider>
	);
};

declare namespace NotificationProvider {
	interface NotificationContextProps {
		success: (
			message: string,
			description: string,
			isClose?: boolean,
		) => void;
		info: (message: string, description: string, isClose?: boolean) => void;
		warn: (message: string, description: string, isClose?: boolean) => void;
		error: (
			message: string,
			description: string,
			isClose?: boolean,
		) => void;
	}

	interface Notification {
		id: number;
		type: "success" | "info" | "warn" | "error";
		message: string;
		description: string;
		isClose?: boolean;
	}
}

export { NotificationProvider, useNotification };
